'use client';

import { Box, List } from '@mui/material';
import { type Dispatch, forwardRef, type SetStateAction } from 'react';
import type { ElementViewedContextValue } from '@repo/segment/Analytics/ElementViewed';
import { Link } from './components/Link';
import { Title } from './components/Title';
import type { TableOfContentsHeadingProps } from './types';
import { useDesktopToc } from './useDesktopToc';

type Props = {
  distanceToSectionWrapper: number;
  headingLevel: number;
  headings: TableOfContentsHeadingProps[];
  tocId: string;
  activeId: string;
  setActiveId: Dispatch<SetStateAction<string>>;
  fireNavigateToSectionPageAction: (
    overrideProps?: Record<string, string | object>,
    callback?: () => void,
  ) => void;
  /** Must be provided from an ElementViewed render-function context. */
  fireElementInteracted: ElementViewedContextValue['fireElementInteracted'];
};

export const Desktop = forwardRef<HTMLElement, Props>(function Desktop(props, ref) {
  const { headings, tocId, activeId } = props;
  const { handleClick, handleRef } = useDesktopToc(props);

  return (
    <Box
      ref={ref}
      id={tocId}
      sx={{
        display: { xs: 'none', md: 'block' },
        width: '100%',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 10px)',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        position: 'sticky',
        top: 10,
      }}
      component="nav"
      aria-label="table of contents"
    >
      <Box
        sx={{
          padding: 2,
          paddingLeft: 3,
          marginBottom: 2,
          borderBottom: '4px solid',
          borderColor: 'primary.main',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'common.white',
        }}
      >
        <Title isHeading />
      </Box>
      <List component="ol" aria-labelledby="on-this-page" disablePadding>
        {headings.map(heading => (
          <List component="li" key={heading.id} disablePadding>
            <Link
              isSelected={activeId === heading.id}
              headingId={heading.id}
              handleRef={handleRef}
              onClick={handleClick}
            >
              {heading.header}
            </Link>
          </List>
        ))}
      </List>
    </Box>
  );
});
